import { InvalidUrlError } from '../../../common/errors/errors';
import { BaseGQLService } from '../../../common/services/BaseGQLService';
import { ModelType } from '../../../common/utils/GQLIdEncoder';
import { IntegrationConnectionResultMapper } from '../../mappers/IntegrationConnectionResultMapper';
import { IntegrationsMapper } from '../../mappers/IntegrationsMapper';
import { IntegrationConnectionResult } from '../../models/IntegrationConnectionResult';
import { JiraExportMapper } from '../mappers/JiraExportMapper';
import { JiraIssueResponseMapper } from '../mappers/JiraIssueResponseMapper';
import { ProjectJiraMapper } from '../mappers/ProjectJiraMapper';
import { JiraExport } from '../models/JiraExport';
import { JiraExportResponse } from '../models/JiraExportResponse';
import { JiraIssueCollector } from '../models/JiraIssueCollector';
import { ProjectJira } from '../models/ProjectJira';
import { BatchExportMapper } from '../../exports/mappers/BatchExportMapper';
import { BatchExport } from '../../exports/models/BatchExport';
import { JiraIssue } from '../models/valueObjects/JiraIssue';

/**
 * Cap the exported issues to 50 due to limitations on the Jira REST API.
 * See https://developer.atlassian.com/cloud/jira/platform/rest/v2/api-group-issues/#api-rest-api-2-issue-bulk-post
 */
const JIRA_EXPORT_LIMIT = 50;

export const GET_REPORT_INSTANCES = `
query($reportId: ID!, $limit: Int!, $keptByFilters: Boolean) {
  node(id: $reportId) {
    ... on ReportDone {
      instances(
        keptByFilters: $keptByFilters
        sorters: [{field: severity, order:DESC}]
        filters: {field: severity, operation: NEQ, value: "PASSED"}
        first: $limit
      ) {
        edges {
          node {
            id
          }
        }
      }
      instancesCount: instances {
        totalCount
      }
    }
  }
}
`;

export const EXPORT_INSTANCE_QUERY = `
mutation($instanceId: ID!){
  jiraExportInstance(input: { instanceId: $instanceId }) {
    jiraUrl
  }
}
`;

export const EXPORT_INSTANCES_QUERY = `
mutation($reportId: ID!, $instanceIds: [ID!]!){
  jiraExportInstances(input: { reportId: $reportId, instanceIds: $instanceIds }) {
    issues {
      issueUrl
    }
    errors {
      errorDetails {
        error
        field
        value
      }
    }
  }
}
`;

export const BATCH_EXPORT_QUERY = `
mutation ($reportId: ID!, $issueIds: [Int!], $instanceIds: [ID!], $keptByFilters: Boolean) {
  jiraExportInstances(
    input: {reportId: $reportId, issueIds: $issueIds, instanceIds: $instanceIds, instancesKeptByFilters: $keptByFilters}
  ) {
    export {
      __typename
      exportType
      status
      totalCount
      exportedCount
      createdAt
      canceledAt
    }
  }
}`;

const JIRA_INTEGRATION_FIELDS = `
url
jiraPublicKey
jiraPrivateKey
jiraConsumerKey
jiraRequestToken
jiraAccessToken
jiraAuthorizationUrl
jiraProjectKey
jiraIssuetypeKey
`;

const JIRA_PROJECT_FIELDS = `
projectKey
issueTypeKey
issueCollectorCode
`;

export const JIRA_INTEGRATION_QUERY = `
query {
  viewer {
    organization {
      jiraIntegration {
        ${JIRA_INTEGRATION_FIELDS}
      }
    }
  }
}
`;

export const UPDATE_JIRA_ISSUE_COLLECTOR_QUERY = `
mutation(
  $issueCollectorCode: String!
) {
  updateJiraIssueCollector(
    input: {
      issueCollectorCode: $issueCollectorCode
    }
  ) {
    organization {
      integrations {
        ...on JiraIssueCollectorIntegration {
          issueCollectorCode
        }
      }
    }
  }
}
`;

export const UPDATE_JIRA_INTEGRATION_URL = `
mutation($url: String!) {
  updateJiraIntegrationUrl(
    input: {
      url: $url
    }
  ) {
    jiraIntegration {
      ${JIRA_INTEGRATION_FIELDS}
    }
  }
}
`;

export const AUTHORIZE_JIRA_INTEGRATION = `
mutation {
  authorizeJiraIntegration(input:{}) {
    jiraIntegration {
      ${JIRA_INTEGRATION_FIELDS}
    }
  }
}
`;

export const REQUEST_JIRA_INTEGRATION_ACCESS_TOKEN = `
mutation {
  requestJiraIntegrationAccessToken(input:{}) {
    jiraIntegration {
      ${JIRA_INTEGRATION_FIELDS}
    }
  }
}
`;

export const UPDATE_JIRA_INTEGRATION = `
mutation($projectKey: String, $issuetypeKey: String) {
  updateJiraIntegration(
    input: {
      projectKey: $projectKey,
      issuetypeKey: $issuetypeKey
    }
  ) {
    jiraIntegration {
      ${JIRA_INTEGRATION_FIELDS}
    }
  }
}
`;

export const UNLINK_JIRA_INTEGRATION = `
mutation {
  unlinkJiraIntegration(input: {}) {
    __typename
  }
}
`;

export const UNLINK_JIRA_ISSUE_COLLECTOR = `
mutation {
  unlinkJiraIssueCollector(input: {}) {
    __typename
  }
}
`;

export const PROJECT_JIRA_QUERY = `
query($projectId: ID!) {
  project: node(id: $projectId) {
    ... on Project {
      projectJira {
        ${JIRA_PROJECT_FIELDS}
      }
    }
  }
}`;

export const UPDATE_PROJECT_JIRA_INTEGRATION = `
mutation($projectId: ID!, $projectKey: String!, $issueTypeKey: String!) {
  updateProjectJiraIntegration(
    input: {
      projectId: $projectId,
      projectKey: $projectKey,
      issueTypeKey: $issueTypeKey
    }
  ) {
    projectJira {
      ${JIRA_PROJECT_FIELDS}
    }
  }
}
`;

export const UPDATE_PROJECT_JIRA_ISSUE_COLLECTOR = `
mutation($projectId: ID!, $issueCollectorCode: String!) {
  updateProjectJiraIssueCollector(
    input: {
      projectId: $projectId,
      issueCollectorCode: $issueCollectorCode
    }
  ) {
    projectJira {
      ${JIRA_PROJECT_FIELDS}
    }
  }
}
`;

export const UNLINK_PROJECT_JIRA_INTEGRATION = `
mutation($projectId: ID!) {
  unlinkProjectJiraIntegration(input: { projectId: $projectId }) {
    __typename
  }
}
`;

export const UNLINK_PROJECT_JIRA_ISSUE_COLLECTOR = `
mutation($projectId: ID!) {
  unlinkProjectJiraIssueCollector(input: { projectId: $projectId }) {
    __typename
  }
}
`;

export const CHECK_JIRA_INTEGRATION = `
mutation(
  $projectKey: String!,
  $issuetypeKey: String!
) {
  checkJiraIntegration(
    input: {
      projectKey: $projectKey,
      issuetypeKey: $issuetypeKey,
    }
  ) {
      jiraConnectionResult {
          isSuccessful
          reason
      }
    }
}
`;

export const CHECK_JIRA_EXPORT = `
mutation(
  $projectKey: String!,
  $issuetypeKey: String!
) {
  checkJiraExport(
    input: {
      projectKey: $projectKey,
      issuetypeKey: $issuetypeKey,
    }
  ) {
      jiraConnectionResult {
          isSuccessful
          reason
      }
    }
}
`;

export const CANCEL_EXPORT = `
mutation ($reportId: ID!) {
  cancelJiraExport(input: {reportId: $reportId}) {
    export {
      __typename
      exportType
      status
      totalCount
      exportedCount
      createdAt
      canceledAt
    }
  }
}`;

export class JiraRepository extends BaseGQLService {
  private readonly jiraIssueMapper = new JiraIssueResponseMapper();
  private readonly integrationsMapper = new IntegrationsMapper();
  private readonly jiraExportMapper = new JiraExportMapper();
  private readonly projectJiraMapper = new ProjectJiraMapper();
  private readonly connectionResultMapper = new IntegrationConnectionResultMapper();
  private readonly exportMapper = new BatchExportMapper();

  async exportInstance(instanceId: number): Promise<JiraExportResponse> {
    return this.mutate(
      EXPORT_INSTANCE_QUERY,
      {
        instanceId: this.gqlIdEncoder.encode(ModelType.Instance, instanceId),
      },
      (data) => {
        const issueUrl = data.jiraExportInstance.jiraUrl;
        return new JiraExportResponse([new JiraIssue(issueUrl)], [], false);
      }
    );
  }

  async batchExport(
    reportId: number,
    issueIds: number[] = [],
    selectedInstancesIds: number[] = [],
    keptByFilters: boolean | null = null
  ): Promise<BatchExport> {
    const gqlReportId = this.gqlIdEncoder.encode(ModelType.Report, reportId);
    const instanceIds = selectedInstancesIds.map((id) => this.gqlIdEncoder.encode(ModelType.Instance, id));
    return this.mutate(
      BATCH_EXPORT_QUERY,
      {
        reportId: gqlReportId,
        instanceIds,
        issueIds,
        keptByFilters: keptByFilters ? null : true,
      },
      (data) => this.exportMapper.mapExport(data.jiraExportInstances.export)
    );
  }

  async exportReport(reportId: number, includeFiltered: boolean): Promise<JiraExportResponse> {
    const gqlReportId = this.gqlIdEncoder.encode(ModelType.Report, reportId);
    return this.query(
      GET_REPORT_INSTANCES,
      {
        reportId: gqlReportId,
        keptByFilters: includeFiltered ? null : true,
        limit: JIRA_EXPORT_LIMIT,
      },
      (data) => {
        const instanceIds = data.node.instances.edges.map((e: any) => e.node.id);
        if (instanceIds.length === 0) {
          return new JiraExportResponse([], [], data.node.instancesCount.totalCount > 0);
        }
        return this.mutate(
          EXPORT_INSTANCES_QUERY,
          {
            reportId: gqlReportId,
            instanceIds,
          },
          (result) => this.jiraIssueMapper.mapGQLModel(result.jiraExportInstances)
        );
      }
    );
  }

  async jiraIntegration(): Promise<JiraExport | null> {
    return this.query(JIRA_INTEGRATION_QUERY, {}, (data) => {
      const { jiraIntegration } = data.viewer.organization;
      if (jiraIntegration) {
        return this.jiraExportMapper.mapGQLModel(jiraIntegration);
      }
      return null;
    });
  }

  async updateJiraIssueCollector(issueCollectorCode: string): Promise<JiraIssueCollector | null> {
    return this.mutate(
      UPDATE_JIRA_ISSUE_COLLECTOR_QUERY,
      {
        issueCollectorCode,
      },
      (data) => {
        const integrations = this.integrationsMapper.mapGQLModel(data.updateJiraIssueCollector.organization.integrations);
        return integrations.getJiraIssueCollector();
      }
    );
  }

  async updateJiraIssueExportUrl(url: string): Promise<JiraExport | null> {
    return this.mutate(
      UPDATE_JIRA_INTEGRATION_URL,
      {
        url,
      },
      (data) => this.jiraExportMapper.mapGQLModel(data.updateJiraIntegrationUrl.jiraIntegration)
    );
  }

  async authorizeJiraIntegration(): Promise<JiraExport | null> {
    return this.mutate(AUTHORIZE_JIRA_INTEGRATION, {}, (data) => this.jiraExportMapper.mapGQLModel(data.authorizeJiraIntegration.jiraIntegration));
  }

  async requestJiraIntegrationAccessToken(): Promise<JiraExport | null> {
    return this.mutate(REQUEST_JIRA_INTEGRATION_ACCESS_TOKEN, {}, (data) =>
      this.jiraExportMapper.mapGQLModel(data.requestJiraIntegrationAccessToken.jiraIntegration)
    );
  }

  async updateJiraIntegration(projectKey: string, issuetypeKey: string): Promise<JiraExport | null> {
    return this.mutate(
      UPDATE_JIRA_INTEGRATION,
      {
        projectKey,
        issuetypeKey,
      },
      (data) => this.jiraExportMapper.mapGQLModel(data.updateJiraIntegration.jiraIntegration)
    );
  }

  async unlinkJiraIntegration(): Promise<void> {
    return this.mutate(UNLINK_JIRA_INTEGRATION, {});
  }

  async unlinkJiraIssueCollector(): Promise<void> {
    return this.mutate(UNLINK_JIRA_ISSUE_COLLECTOR, {});
  }

  async getProjectJira(projectId: number): Promise<ProjectJira | null> {
    return this.query(
      PROJECT_JIRA_QUERY,
      {
        projectId: this.gqlIdEncoder.encode(ModelType.Project, projectId),
      },
      (data) => {
        if (!data.project) {
          throw new InvalidUrlError(`Invalid request: could not find Project ${projectId}`);
        }
        const { projectJira } = data.project;
        if (projectJira) {
          return this.projectJiraMapper.mapGQLModel(projectJira);
        }
        return null;
      }
    );
  }

  async updateProjectJiraIntegration(projectId: number, projectKey: string, issueTypeKey: string): Promise<ProjectJira | null> {
    return this.mutate(
      UPDATE_PROJECT_JIRA_INTEGRATION,
      {
        projectId: this.gqlIdEncoder.encode(ModelType.Project, projectId),
        projectKey,
        issueTypeKey,
      },
      (data) => this.projectJiraMapper.mapGQLModel(data.updateProjectJiraIntegration.projectJira)
    );
  }

  async updateProjectJiraIssueCollector(projectId: number, issueCollectorCode: string): Promise<ProjectJira | null> {
    return this.mutate(
      UPDATE_PROJECT_JIRA_ISSUE_COLLECTOR,
      {
        projectId: this.gqlIdEncoder.encode(ModelType.Project, projectId),
        issueCollectorCode,
      },
      (data) => this.projectJiraMapper.mapGQLModel(data.updateProjectJiraIssueCollector.projectJira)
    );
  }

  async unlinkProjectJiraIntegration(projectId: number): Promise<void> {
    return this.mutate(UNLINK_PROJECT_JIRA_INTEGRATION, {
      projectId: this.gqlIdEncoder.encode(ModelType.Project, projectId),
    });
  }

  async unlinkProjectJiraIssueCollector(projectId: number): Promise<void> {
    return this.mutate(UNLINK_PROJECT_JIRA_ISSUE_COLLECTOR, {
      projectId: this.gqlIdEncoder.encode(ModelType.Project, projectId),
    });
  }

  async checkJiraIntegration(projectKey: string, issuetypeKey: string): Promise<IntegrationConnectionResult> {
    return this.mutate(
      CHECK_JIRA_INTEGRATION,
      {
        projectKey,
        issuetypeKey,
      },
      (data) => this.connectionResultMapper.mapGQLModel(data.checkJiraIntegration.jiraConnectionResult)
    );
  }

  async checkJiraExport(projectKey: string, issuetypeKey: string): Promise<IntegrationConnectionResult> {
    return this.mutate(
      CHECK_JIRA_EXPORT,
      {
        projectKey,
        issuetypeKey,
      },
      (data) => this.connectionResultMapper.mapGQLModel(data.checkJiraExport.jiraConnectionResult)
    );
  }

  async cancelBatchExport(reportId: number) {
    return this.mutate(
      CANCEL_EXPORT,
      {
        reportId: this.gqlIdEncoder.encode(ModelType.Report, reportId),
      },
      (data) => {
        return this.exportMapper.mapExport(data.cancelJiraExport.export);
      }
    );
  }
}
